import { useQueries } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { Video, Users, Tag as TagIcon, Inbox } from "lucide-react"
import { api, type CatalogResponse, type PermissionRequest, type Tag } from "@/lib/types"
import { GlarchiveSubNav } from "@/components/GlarchiveSubNav"

export function AdminDashboard() {
  const [recentVideos, users, tags, requests] = useQueries({
    queries: [
      { queryKey: ["adminVideosOverview"], queryFn: () => api<CatalogResponse>("/api/admin/videos", { query: { limit: 5 } }) },
      { queryKey: ["adminUsersCount"], queryFn: () => api<{ items: unknown[]; total: number }>("/api/admin/users", { query: { limit: 1 } }) },
      { queryKey: ["adminTagsAll"], queryFn: () => api<{ tags: Tag[] }>("/api/admin/tags") },
      { queryKey: ["adminPendingRequests"], queryFn: () => api<{ items: PermissionRequest[]; total: number }>("/api/admin/requests", { query: { status: "pending" } }) },
    ],
  })

  return (
    <div className="space-y-6">
      <GlarchiveSubNav />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card to="/admin/videos" icon={<Video size={16} />} label="Recent videos" value={recentVideos.isLoading ? "…" : String(recentVideos.data?.items.length ?? 0)} hint="last 5" />
        <Card to="/admin/users" icon={<Users size={16} />} label="Users" value={users.isLoading ? "…" : String(users.data?.total ?? 0)} hint="total" />
        <Card to="/admin/tags" icon={<TagIcon size={16} />} label="Tags" value={tags.isLoading ? "…" : String(tags.data?.tags.length ?? 0)} hint="defined" />
        <Card to="/admin/requests" icon={<Inbox size={16} />} label="Pending requests" value={requests.isLoading ? "…" : String(requests.data?.total ?? 0)} hint="awaiting review" />
      </div>

      <section className="glass-section">
        <h2 className="font-heading text-xl font-semibold">Pending requests</h2>
        <p className="mt-1 text-sm text-th-fg/60">Users waiting for tag grants.</p>
        <div className="mt-4 space-y-2">
          {requests.data?.items.length ? (
            requests.data.items.slice(0, 5).map((r) => (
              <Link
                key={r.id}
                to="/admin/requests"
                className="flex items-start justify-between rounded-xl border border-th-glass/[0.08] bg-th-glass/[0.03] p-3 hover:bg-th-glass/[0.06]"
              >
                <div>
                  <div className="font-medium text-th-fg">{r.user_name || r.user_email}</div>
                  <div className="text-xs text-th-fg/50">{r.user_email}</div>
                  {r.message && <div className="mt-1 text-sm text-th-fg/70 line-clamp-2">{r.message}</div>}
                </div>
                <span className="tag-badge">Review →</span>
              </Link>
            ))
          ) : (
            <div className="text-sm text-th-fg/40">Nothing waiting. Inbox zero.</div>
          )}
        </div>
      </section>
    </div>
  )
}

function Card({ to, icon, label, value, hint }: { to: string; icon: React.ReactNode; label: string; value: string; hint: string }) {
  return (
    <Link to={to} className="glass-card-hover flex flex-col gap-1 p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-th-fg/50">
        {icon} {label}
      </div>
      <div className="font-heading text-3xl font-semibold text-th-fg">{value}</div>
      <div className="text-xs text-th-fg/40">{hint}</div>
    </Link>
  )
}
